import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { Blog, ContentSource, PipelineStepRunState } from '../../types';

@Component({
    standalone: false,
    selector: 'mkt-pipeline-source-card',
    template: `
    <div class="ps-card" [class.ps-card--running]="runState?.status === 'running'"
         [class.ps-card--done]="runState?.status === 'completed'"
         [class.ps-card--failed]="runState?.status === 'failed'">

      <!-- Header -->
      <div class="ps-card__header" (click)="expanded = !expanded">
        <div class="ps-card__icon" style="color: var(--mj-color-indigo-500)">
          <i class="fa-solid fa-file-lines"></i>
        </div>
        <div class="ps-card__title">Source</div>
        <div class="ps-card__badge" *ngIf="selectedBlogIds.length > 0 || selectedSourceIds.length > 0">
          {{ selectedBlogIds.length + selectedSourceIds.length }}
        </div>
        <div class="ps-card__status">
          <span class="ps-status-dot" [class]="'ps-status-dot--' + (runState?.status || 'pending')"></span>
        </div>
        <button class="ps-card__chevron">
          <i class="fa-solid" [class.fa-chevron-down]="!expanded" [class.fa-chevron-up]="expanded"></i>
        </button>
      </div>

      <!-- Expanded body -->
      <div class="ps-card__body" *ngIf="expanded">

        <!-- Source mode toggle -->
        <div class="ps-source-toggle">
          <button class="ps-toggle-btn" [class.ps-toggle-btn--active]="sourceMode === 'blogs'"
                  (click)="sourceMode = 'blogs'">
            <i class="fa-solid fa-blog"></i> Published Blogs
          </button>
          <button class="ps-toggle-btn" [class.ps-toggle-btn--active]="sourceMode === 'sources'"
                  (click)="sourceMode = 'sources'">
            <i class="fa-solid fa-podcast"></i> Content Sources
          </button>
        </div>

        <!-- Search -->
        <input class="ps-search" [(ngModel)]="searchText" placeholder="Filter..." />

        <!-- Blog list -->
        <div class="ps-list" *ngIf="sourceMode === 'blogs'">
          <div *ngIf="filteredBlogs.length === 0" class="ps-list__empty">
            No published blogs found.
          </div>
          <label *ngFor="let b of filteredBlogs" class="ps-list__item">
            <input type="checkbox" [checked]="selectedBlogIds.includes(b.ID)"
                   (change)="toggleBlog(b.ID)" />
            <span class="ps-list__text">{{ b.Title }}</span>
          </label>
        </div>

        <!-- Content source list -->
        <div class="ps-list" *ngIf="sourceMode === 'sources'">
          <div *ngIf="filteredSources.length === 0" class="ps-list__empty">
            No content sources found.
          </div>
          <label *ngFor="let cs of filteredSources" class="ps-list__item">
            <input type="checkbox" [checked]="selectedSourceIds.includes(cs.ID)"
                   (change)="toggleSource(cs.ID)" />
            <span class="ps-list__text">{{ cs.Title || 'Untitled Source' }}</span>
            <span class="ps-list__meta" *ngIf="cs.SourcePlatform">{{ cs.SourcePlatform }}</span>
          </label>
        </div>

        <!-- Selection summary -->
        <div class="ps-summary" *ngIf="selectedBlogIds.length > 0 || selectedSourceIds.length > 0">
          <span *ngIf="selectedBlogIds.length > 0">{{ selectedBlogIds.length }} blog{{ selectedBlogIds.length > 1 ? 's' : '' }}</span>
          <span *ngIf="selectedBlogIds.length > 0 && selectedSourceIds.length > 0"> + </span>
          <span *ngIf="selectedSourceIds.length > 0">{{ selectedSourceIds.length }} source{{ selectedSourceIds.length > 1 ? 's' : '' }}</span>
          <span> selected</span>
          <button class="ps-clear-btn" (click)="clearSelection()">Clear</button>
        </div>
      </div>

      <!-- Run state output -->
      <div class="ps-card__run-output" *ngIf="runState?.outputSummary">
        <i class="fa-solid fa-check" style="color: var(--mj-color-success-400); margin-right: 4px"></i>
        {{ runState!.outputSummary }}
      </div>
      <div class="ps-card__run-error" *ngIf="runState?.error">
        <i class="fa-solid fa-xmark" style="color: var(--mj-status-error); margin-right: 4px"></i>
        {{ runState!.error }}
      </div>
    </div>
    `,
    styles: [`
      .ps-card { background: var(--mj-bg-surface); border: 1px solid var(--mj-border-default); border-radius: 10px; min-width: 220px; max-width: 320px; transition: all 0.2s; }
      .ps-card--running { border-color: var(--mj-color-indigo-500); box-shadow: 0 0 12px color-mix(in srgb, var(--mj-color-indigo-500) 20%, transparent); }
      .ps-card--done { border-color: var(--mj-color-success-400); }
      .ps-card--failed { border-color: var(--mj-status-error); }

      .ps-card__header { display: flex; align-items: center; gap: 8px; padding: 10px 12px; cursor: pointer; }
      .ps-card__icon { font-size: 1rem; width: 24px; text-align: center; }
      .ps-card__title { font-size: 0.82rem; font-weight: 700; color: var(--mj-text-primary); flex: 1; }
      .ps-card__badge { font-size: 0.65rem; background: var(--mj-color-indigo-500); color: var(--mj-text-inverse); border-radius: 10px; padding: 1px 7px; font-weight: 700; }
      .ps-card__chevron { background: none; border: none; color: var(--mj-text-disabled); cursor: pointer; font-size: 0.7rem; padding: 2px; }

      .ps-card__body { padding: 0 12px 12px; display: flex; flex-direction: column; gap: 8px; }

      .ps-source-toggle { display: flex; gap: 0; border-radius: 6px; overflow: hidden; border: 1px solid var(--mj-border-default); }
      .ps-toggle-btn { flex: 1; padding: 5px 8px; font-size: 0.72rem; font-weight: 600; border: none; background: var(--mj-bg-page); color: var(--mj-text-secondary); cursor: pointer; display: flex; align-items: center; gap: 4px; justify-content: center; transition: all 0.15s; }
      .ps-toggle-btn--active { background: var(--mj-bg-surface); color: var(--mj-color-indigo-500); }
      .ps-toggle-btn i { font-size: 0.68rem; }

      .ps-search { font-size: 0.78rem; padding: 5px 8px; border-radius: 5px; border: 1px solid var(--mj-border-default); background: var(--mj-bg-page); color: var(--mj-text-primary); outline: none; width: 100%; }
      .ps-search:focus { border-color: var(--mj-color-indigo-500); }

      .ps-list { max-height: 180px; overflow-y: auto; display: flex; flex-direction: column; gap: 2px; }
      .ps-list__empty { font-size: 0.75rem; color: var(--mj-text-disabled); padding: 8px 0; text-align: center; }
      .ps-list__item { display: flex; align-items: center; gap: 6px; padding: 4px 6px; border-radius: 4px; font-size: 0.78rem; color: var(--mj-text-primary); cursor: pointer; transition: background 0.1s; }
      .ps-list__item:hover { background: var(--mj-bg-page); }
      .ps-list__item input { width: 14px; height: 14px; cursor: pointer; accent-color: var(--mj-color-indigo-500); }
      .ps-list__text { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .ps-list__meta { font-size: 0.68rem; color: var(--mj-text-disabled); }

      .ps-summary { font-size: 0.72rem; color: var(--mj-text-secondary); display: flex; align-items: center; gap: 4px; padding-top: 4px; border-top: 1px solid var(--mj-border-default); }
      .ps-clear-btn { background: none; border: none; color: var(--mj-color-indigo-500); cursor: pointer; font-size: 0.72rem; font-weight: 600; margin-left: auto; }

      .ps-card__run-output, .ps-card__run-error { font-size: 0.72rem; padding: 6px 12px; border-top: 1px solid var(--mj-border-default); }
      .ps-card__run-output { color: var(--mj-color-success-400); }
      .ps-card__run-error { color: var(--mj-status-error); }

      .ps-status-dot { display: inline-block; width: 7px; height: 7px; border-radius: 50%; }
      .ps-status-dot--pending { background: var(--mj-text-disabled); }
      .ps-status-dot--running { background: var(--mj-color-indigo-500); animation: ps-pulse 1.5s infinite; }
      .ps-status-dot--completed { background: var(--mj-color-success-400); }
      .ps-status-dot--failed { background: var(--mj-status-error); }
      .ps-status-dot--skipped { background: var(--mj-text-disabled); opacity: 0.4; }
      @keyframes ps-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
    `]
})
export class PipelineSourceCardComponent implements OnChanges {
    @Input() blogs: Blog[] = [];
    @Input() contentSources: ContentSource[] = [];
    @Input() runState: PipelineStepRunState | null = null;

    @Output() selectionChanged = new EventEmitter<{ blogIds: string[]; sourceIds: string[] }>();

    expanded = true;
    sourceMode: 'blogs' | 'sources' = 'blogs';
    searchText = '';
    selectedBlogIds: string[] = [];
    selectedSourceIds: string[] = [];

    get filteredBlogs(): Blog[] {
        const q = this.searchText.toLowerCase();
        const published = this.blogs.filter(b => {
            const status = (b as any).Status || (b as any).BlogStatus || '';
            return status.toLowerCase() === 'published';
        });
        if (!q) return published;
        return published.filter(b => b.Title?.toLowerCase().includes(q));
    }

    get filteredSources(): ContentSource[] {
        const q = this.searchText.toLowerCase();
        if (!q) return this.contentSources;
        return this.contentSources.filter(cs =>
            (cs.Title || '').toLowerCase().includes(q) ||
            (cs.SourcePlatform || '').toLowerCase().includes(q)
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        // If blogs list changes, prune stale selections
        if (changes['blogs'] && this.selectedBlogIds.length > 0) {
            const ids = new Set(this.blogs.map(b => b.ID));
            this.selectedBlogIds = this.selectedBlogIds.filter(id => ids.has(id));
        }
    }

    toggleBlog(id: string): void {
        const idx = this.selectedBlogIds.indexOf(id);
        if (idx >= 0) {
            this.selectedBlogIds.splice(idx, 1);
        } else {
            this.selectedBlogIds.push(id);
        }
        this.emitSelection();
    }

    toggleSource(id: string): void {
        const idx = this.selectedSourceIds.indexOf(id);
        if (idx >= 0) {
            this.selectedSourceIds.splice(idx, 1);
        } else {
            this.selectedSourceIds.push(id);
        }
        this.emitSelection();
    }

    clearSelection(): void {
        this.selectedBlogIds = [];
        this.selectedSourceIds = [];
        this.emitSelection();
    }

    private emitSelection(): void {
        this.selectionChanged.emit({
            blogIds: [...this.selectedBlogIds],
            sourceIds: [...this.selectedSourceIds],
        });
    }
}
